"use client"

import React, { useRef } from "react";
import { Download, ArrowRight, BarChart2, LineChart, TrendingUp, Play, Globe2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { motion, useInView } from "framer-motion";
import { cn } from "../../lib/utils";
import { Card, CardContent } from "../../components/ui/card";
import PagentMoreThanSocialMedia from "./PagentMoreThanSocialMedia";

// Constants for app download links
const APPLE_APP_STORE_URL = "https://apps.apple.com/ca/app/pagent/id6670614673";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.pagent";

function ElegantShape({
  className,
  delay = 0,
  width = 400,
  height = 100,
  rotate = 0,
  gradient = "from-white/[0.08]",
}) {
  return (
    <motion.div
      initial={{
        opacity: 0,
        y: -150,
        rotate: rotate - 15,
      }}
      animate={{
        opacity: 1,
        y: 0,
        rotate: rotate,
      }}
      transition={{
        duration: 2.4,
        delay,
        ease: [0.23, 0.86, 0.39, 0.96],
        opacity: { duration: 1.2 },
      }}
      className={cn("absolute", className)}
    >
      <motion.div
        animate={{
          y: [0, 15, 0],
        }}
        transition={{
          duration: 12,
          repeat: Number.POSITIVE_INFINITY,
          ease: "easeInOut",
        }}
        style={{
          width,
          height,
        }}
        className="relative"
      >
        <div
          className={cn(
            "absolute inset-0 rounded-full",
            "bg-gradient-to-r to-transparent",
            gradient,
            "backdrop-blur-[2px] border-2 border-white/[0.15]",
            "shadow-[0_8px_32px_0_rgba(255,255,255,0.1)]",
            "after:absolute after:inset-0 after:rounded-full",
            "after:bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.2),transparent_70%)]",
          )}
        />
      </motion.div>
    </motion.div>
  );
}

function FeatureCard({ feature, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.2 });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ 
        duration: 0.7, 
        delay: index * 0.15,
        ease: [0.25, 0.4, 0.25, 1] 
      }}
    >
      <Card className="overflow-hidden border-zinc-200 shadow-sm hover:shadow-md transition-all h-full">
        <CardContent className="p-6 flex flex-col h-full">
          <div className="mb-4">{feature.icon}</div>
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-yellow-100 flex items-center justify-center mt-1">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-600"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-zinc-900">{feature.title}</h3>
            </div>
            <p className="text-zinc-600">{feature.description}</p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function HeroSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  const fadeUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        delay: 0.5 + i * 0.2,
        ease: [0.25, 0.4, 0.25, 1],
      },
    }),
  };

  return (
    <section className="relative pb-20 overflow-hidden bg-gradient-to-b from-gray-950 to-gray-900 text-white">
      {/* Desktop Phone Image - Subtle directional glow */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.8 }}
        className="absolute top-32 right-[5%] lg:right-[10%] z-20 w-[300px] md:w-[350px] lg:w-[450px] hidden md:block"
      >
        {/* Top-left glow */}
        <div className="absolute -top-10 -left-10 w-3/4 h-3/4 bg-gradient-to-br from-yellow-500/20 to-transparent rounded-full blur-xl" />
        
        {/* Bottom-right glow - Enhanced */}
        <div className="absolute -bottom-12 -right-12 w-full h-full bg-gradient-to-tl from-yellow-400/25 to-transparent rounded-full blur-xl" />
        
        {/* Desktop image */}
        <img 
          src="/images/Herophoto.png" 
          alt="Pagent Competition App" 
          className="relative z-10 w-full h-auto object-contain rounded-xl"
        />
      </motion.div>

      {/* Hero Section with Geometric Background */}
      <section className="relative w-full overflow-hidden bg-zinc-900 h-[820px] sm:h-[880px] flex items-center pt-0 mt-0">
        {/* Geometric Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/[0.03] via-transparent to-yellow-500/[0.03] blur-2xl" />

        <div className="absolute inset-0 overflow-hidden">
          {/* Keep existing ElegantShape components for desktop */}
          <div className="hidden md:block">
            <ElegantShape
              delay={0.3}
              width={600}
              height={140}
              rotate={12}
              gradient="from-yellow-500/[0.1]"
              className="left-[-10%] md:left-[-5%] top-[15%] md:top-[20%]"
            />

            <ElegantShape
              delay={0.5}
              width={500}
              height={120}
              rotate={-15}
              gradient="from-yellow-600/[0.1]"
              className="right-[-5%] md:right-[0%] top-[70%] md:top-[75%]"
            />

            <ElegantShape
              delay={0.4}
              width={300}
              height={80}
              rotate={-8}
              gradient="from-yellow-500/[0.1]"
              className="left-[5%] md:left-[10%] bottom-[5%] md:bottom-[10%]"
            />

            <ElegantShape
              delay={0.6}
              width={200}
              height={60}
              rotate={20}
              gradient="from-yellow-400/[0.1]"
              className="right-[15%] md:right-[20%] top-[10%] md:top-[15%]"
            />

            <ElegantShape
              delay={0.7}
              width={150}
              height={40}
              rotate={-25}
              gradient="from-yellow-300/[0.1]"
              className="left-[20%] md:left-[25%] top-[5%] md:top-[10%]"
            />
          </div>
          
          {/* Simplified and optimized shapes for mobile */}
          <div className="block md:hidden">
            <ElegantShape
              delay={0.3}
              width={300}
              height={80}
              rotate={12}
              gradient="from-yellow-500/[0.1]"
              className="left-[-15%] top-[10%]"
            />

            <ElegantShape
              delay={0.5}
              width={250}
              height={60}
              rotate={-15}
              gradient="from-yellow-600/[0.1]"
              className="right-[-10%] top-[65%]"
            />

            <ElegantShape
              delay={0.4}
              width={200}
              height={50}
              rotate={-8}
              gradient="from-yellow-500/[0.1]"
              className="left-[0%] bottom-[5%]"
            />
          </div>
        </div>

        {/* Hero Content - Adjusted for better mobile experience */}
        <div className="container relative z-10 pt-0 -mt-16">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="absolute -top-32 -bottom-64 -left-32 -right-32 -mx-8 lg:-mx-24 bg-black/30 backdrop-blur-md rounded-[3rem]" 
          />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 p-8">
            {/* Hero Left Content */}
            <motion.div 
              initial="hidden"
              animate="visible"
              className="space-y-10 lg:col-span-7"
            >
              <div className="relative space-y-10">
                <motion.div custom={0} variants={fadeUpVariants} className="space-y-6">
                  <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl">
                    Video & Live{" "}
                    <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                      Competitions
                    </span>
                  </h1>
                  <motion.p custom={1} variants={fadeUpVariants} className="max-w-[42rem] leading-normal text-zinc-200 sm:text-xl sm:leading-8">
                    Welcome to the next level of social competition. Show off your skills, take on exciting challenges, and build your following—all in one place.
                  </motion.p>
                </motion.div>
                
                {/* Mobile-optimized buttons with larger touch targets */}
                <motion.div custom={2} variants={fadeUpVariants} className="flex flex-wrap gap-4">
                  <a href={APPLE_APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105">
                    <div className="rounded-lg overflow-hidden" style={{ maxWidth: '180px' }}>
                      <img 
                        src="/images/AppStoreFig.png" 
                        alt="Download on App Store" 
                        className="h-auto w-full object-contain"
                      />
                    </div>
                  </a>
                  <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105">
                    <div className="rounded-lg overflow-hidden" style={{ maxWidth: '180px' }}>
                      <img 
                        src="/images/GoogleStoreFig.png" 
                        alt="Get it on Google Play" 
                        className="h-auto w-full object-contain"
                      />
                    </div>
                  </a>
                </motion.div>
                
                {/* Responsive grid for mobile */}
                <motion.div custom={3} variants={fadeUpVariants} className="grid grid-cols-3 gap-4 md:gap-8 pt-6">
                  <div className="space-y-2 md:space-y-3">
                    <h4 className="text-3xl md:text-4xl font-bold text-yellow-400">Fun</h4>
                    <p className="text-xs md:text-sm text-zinc-200">Exciting Challenges</p>
                  </div>
                  <div className="space-y-2 md:space-y-3">
                    <h4 className="text-3xl md:text-4xl font-bold text-yellow-400">Social</h4>
                    <p className="text-xs md:text-sm text-zinc-200">Meet New People</p>
                  </div>
                  <div className="space-y-2 md:space-y-3">
                    <h4 className="text-3xl md:text-4xl font-bold text-yellow-400">Win</h4>
                    <p className="text-xs md:text-sm text-zinc-200">Amazing Prizes</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Empty space for phone image */}
            <div className="lg:col-span-5"></div>
          </div>
        </div>

        {/* Sharp transition instead of blur gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-white pointer-events-none" />
      </section>

      {/* Mobile image positioned between sections */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.8 }}
        className="relative z-30 mx-auto block md:hidden -mt-40 mb-8 w-[280px]"
      >
        {/* Enhanced glow effects for mobile image */}
        <div className="absolute -top-12 -left-12 w-full h-full bg-gradient-to-br from-yellow-500/40 to-transparent rounded-full blur-2xl" />
        <div className="absolute -bottom-16 -right-16 w-[120%] h-[120%] bg-gradient-to-tl from-yellow-400/50 via-yellow-500/30 to-transparent rounded-full blur-2xl" />
        
        {/* Additional subtle glow for depth */}
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-300/20 via-transparent to-yellow-400/20 rounded-full blur-xl" />
        
        {/* Mobile image - voteIMG.png with mobile-oriented styling */}
        <img 
          src="/images/mobileHero.png" 
          alt="Pagent Mobile App" 
          className="relative z-10 w-full h-auto object-contain rounded-xl transform rotate-[-5deg] scale-110 drop-shadow-[0_10px_25px_rgba(234,179,8,0.25)]"
        />
      </motion.div>

      {/* Replace Features Section with PagentMoreThanSocialMedia component */}
      <div id="features" className="relative mt-[-30px] md:mt-[-100px]">
        <div className="container relative z-10 px-6 sm:px-8 md:px-10">
          <div className="max-w-3xl text-left mb-8 mt-8 pt-16 md:mt-16 md:pt-24">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl text-zinc-900 mb-4">
              Not Just Social Media
            </h2>
            <p className="text-xl text-zinc-600">
              Pagent offers a complete platform for talent showcase, competition, and connection
            </p>
          </div>
        </div>
        <PagentMoreThanSocialMedia />
      </div>
    </section>
  );
}